/**
 * Legacy API service compatibility layer
 * Provides backward compatibility while using new clean architecture
 */

// Import new architecture services
import { eventDataService } from '../application/EventDataService.js'
import { runnerTrackingService } from '../application/RunnerTrackingService.js'

/**
 * Fetch event data including markers and route URL
 * @param {string} eventAlias - The event alias/name
 * @returns {Promise<Object>} Event data from API
 * @deprecated Use eventDataService.getEventData() instead
 */
export async function fetchEventData(eventAlias) {
  return await eventDataService.getEventData(eventAlias)
}

/**
 * Load GPX route data from URL
 * @param {string} routeUrl - URL to GPX file
 * @returns {Promise<Array>} Array of route points [{latitude, longitude}]
 * @deprecated Use loadGpxRoute from utils/gpxUtils.js instead
 */
export async function loadGpxRoute(routeUrl) {
  const { loadGpxRoute: newLoadGpxRoute } = await import('../utils/gpxUtils.js')
  return await newLoadGpxRoute(routeUrl)
}

/**
 * Check if track data contains elevation information
 * @param {Array} tracks - Array of track points
 * @returns {boolean} True if elevation data is available
 * @deprecated Use hasElevationData from utils/gpsUtils.js instead
 */
export async function hasElevationData(tracks) {
  const { hasElevationData: newHasElevationData } = await import('../utils/gpsUtils.js')
  return newHasElevationData(tracks)
}

/**
 * Fetch runner data from the live API
 * @param {string} eventAlias - The event alias/name
 * @param {string|number} runnerNumber - The runner's bib number
 * @param {Object} options - Optional query parameters
 * @returns {Promise<Object>} Runner data from API
 * @deprecated Use runnerTrackingService.getRunnerTrackingData() instead
 */
export async function fetchRunnerData(eventAlias, runnerNumber, options = {}) {
  const { runnerApiClient } = await import('../infrastructure/ApiClient.js')
  return await runnerApiClient.getRunnerData(eventAlias, runnerNumber, options)
}

/**
 * Transform API response to format expected by the UI components
 * @param {Object} apiData - Raw API response data
 * @returns {Object} Transformed data for UI components
 * @deprecated Use runnerTrackingService.transformToUIData() instead
 */
export async function transformRunnerData(apiData) {
  return runnerTrackingService.transformToUIData(apiData)
}

/**
 * Calculate distance between two GPS points using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point  
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in kilometers
 * @deprecated Use haversineDistance from utils/gpsUtils.js instead
 */
export async function haversineDistance(lat1, lon1, lat2, lon2) {
  const { haversineDistance: newHaversineDistance } = await import('../utils/gpsUtils.js')
  return newHaversineDistance(lat1, lon1, lat2, lon2)
}

// Remove all the large functions that have been moved to other layers
// Keep only the compatibility exports